import BaseLayout from "../layouts/BaseLayout";

const Section = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div style={{ marginBottom: "32px" }}>
    <h4
      style={{
        fontWeight: 600,
        fontSize: "14px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#6b7280",
        marginBottom: "12px",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: "6px"
      }}
    >
      {title}
    </h4>
    {children}
  </div>
);

const Term = ({ term, def }: { term: string; def: string }) => (
  <div style={{ marginBottom: "10px", lineHeight: "1.5" }}>
    <strong style={{ color: "#2B1460" }}>{term}</strong>
    <span style={{ color: "#374151" }}> — {def}</span>
  </div>
);

export function Glossary(): React.ReactElement {
  return (
    <BaseLayout pageTitle="UX Glossary">
      <div style={{ maxWidth: "800px", paddingTop: "24px" }}>
        <h3 style={{ fontWeight: 700, fontSize: "22px", marginBottom: "4px" }}>
          UX Glossary — Full Internal Reference
        </h3>
        <p style={{ color: "#6b7280", marginBottom: "32px", fontSize: "14px" }}>
          Complete glossary of all platform terms, statuses, roles, and copy.
          Maintained alongside{" "}
          <code>frontend/app/src/pages/Learn/Glossary.tsx</code> (user-facing)
          and <code>docs/ux-glossary.md</code> (source of truth).
        </p>

        <Section title="The Platform">
          <Term term="Vojomo" def="The strategic operating platform." />
          <Term
            term="Vo"
            def="Vojomo's embedded AI thought partner. Researches, synthesizes, and writes alongside users throughout the platform."
          />
          <Term
            term="VoChat"
            def="Streamlined AI chat interface (Beta) for direct conversation with Vo."
          />
          <Term
            term="VoPersonalAssistant"
            def="Internal agent type powering personal AI tasks."
          />
          <Term
            term="QBRScoringAgency"
            def="Internal AI agent for QBR scoring tasks."
          />
        </Section>

        <Section title="Roadmap Building Blocks (The 7)">
          <Term
            term="Roadmap"
            def='The central strategic artifact in Vojomo. Built from 7 Building Blocks. Can be a "Vojomo Book" type.'
          />
          <Term
            term="Building Block"
            def="One of the 7 strategic components of a roadmap. Also a document type (internal type string: building-block)."
          />
          <Term
            term="Vision"
            def='Starts with "We envision a world where…" — the future free from the problem being solved.'
          />
          <Term
            term="Mission"
            def="What the company does today to advance the vision."
          />
          <Term
            term="Peak Ambition"
            def="A realistic yet ambitious future headline with a specific date and publication name."
          />
          <Term
            term="Peak Ambition Objectives"
            def="Outcomes that must be true to achieve the Peak Ambition."
          />
          <Term
            term="3 Year Objectives"
            def="Measurable outcomes the company must accomplish within three years."
          />
          <Term
            term="1 Year Objectives"
            def="Specific outcomes required this year to progress toward 3-Year Objectives."
          />
          <Term
            term="Quarterly Objectives"
            def='Also called "Next Quarter." The immediate outcomes the team is focused on this quarter.'
          />
          <Term
            term="Session"
            def="A working session inside the Roadmap Builder where Building Blocks are developed with Vo."
          />
          <Term
            term="Block"
            def='A single unit of a roadmap (one of 7). Progress shown as "X / 7 blocks."'
          />
          <Term
            term="Gap"
            def="An unfilled or incomplete section within a roadmap block."
          />
          <Term
            term="Assumption"
            def="AI-reasoned content Vo has filled in on the user's behalf. Clearly labeled for review."
          />
          <Term
            term="Contribution / Solo Contribution"
            def="A user's individual input to a shared roadmap session."
          />
          <Term
            term="Shared Roadmap"
            def="A roadmap another user has invited you to collaborate on."
          />
          <Term
            term="My Roadmaps / Team Roadmaps"
            def="My Roadmaps: page at /roadmap/myroadmaps listing all owned sessions. Team Roadmaps: sub-section for sessions where the user has team_member access."
          />
          <Term
            term="Mountain View"
            def="Visual SVG diagram of a session's 7 building blocks. Path: /roadmap/mountain/:sessionId. Inline editing via pencil → textarea → PATCH /api/rb/sessions/:id/blocks."
          />
          <Term
            term="NQO / Next Quarter Objectives"
            def="Always-on progress type for planning the next quarter. period_type: 'nqo' in progress_rounds. Vo converts raw notes to WMBT objectives. Includes Quarter Navigator (prev/next pill)."
          />
          <Term
            term="AAR / After Action Review"
            def="Structured reflection tool. Backend: AARSession + AARResponse models, aar_sessions collection, aar_router in app.py."
          />
        </Section>

        <Section title="Studio, Artifacts & Documents">
          <Term
            term="Studio"
            def="Navigation section (formerly labeled 'Documents') at /documents. Houses all content artifacts: text documents, alignment reports, and progress syntheses."
          />
          <Term
            term="Artifact"
            def="Any piece of content produced in or associated with a session. Types: text document, alignment report, progress synthesis."
          />
          <Term
            term="Alignment Report / TAR"
            def="AI-generated Team Alignment Report synthesizing team contributor scores and notes into a structured comparison. Visible to advisors and session owners. TAR is the internal abbreviation used in UI code."
          />
          <Term
            term="Progress Synthesis / Synthesis Report"
            def="AI-generated synthesis of progress data for a period, compiled from team contributions and released by the session owner or advisor."
          />
          <Term
            term="Document"
            def="Any piece of written content (Topic or Briefing Doc), organized into folders."
          />
          <Term
            term="Topic"
            def="The primary document type with rich text editor and tabs: Topic, Sources, Roadmap, Briefing Doc. Internal type string: building-block."
          />
          <Term
            term="Briefing Doc"
            def="A structured summary document type. Internal type string: briefing-doc."
          />
          <Term
            term="Source"
            def="A reference file or URL attached to a document. Provides grounding context for Vo."
          />
          <Term
            term="Output"
            def="AI-generated content produced in response to a prompt or workflow task."
          />
          <Term
            term="Folder"
            def="Organizes related documents in the Document Library."
          />
        </Section>

        <Section title="Workflows & Coordinator">
          <Term
            term="Coordinator"
            def="The admin section for managing structured workflows across a team."
          />
          <Term
            term="Workflow"
            def="A structured assignment sent to participants. Statuses: Draft, Active, Completed."
          />
          <Term
            term="Module"
            def="The type of task a workflow is built around. Options: QBR Scoring, Roadmap Builder, Topic Document."
          />
          <Term
            term="Participant"
            def="A user assigned to complete a workflow. Statuses: Pending, Completed, Overdue."
          />
          <Term
            term="Assignment"
            def="The specific task given to a participant, may include a URL."
          />
          <Term
            term="Deadline"
            def="Date by which a participant must complete their assignment. Missed = Overdue."
          />
          <Term
            term="Dynamic tokens"
            def="Placeholders in workflow email templates: {First Name}, {Deadline}, {Assignment Link}."
          />
          <Term
            term="Delivery channels"
            def="How workflow notifications are sent: In-app notification, Email."
          />
        </Section>

        <Section title="Statuses & States">
          <Term
            term="Workflow statuses"
            def="Draft · Active · Completed"
          />
          <Term
            term="Participant statuses"
            def="Pending · Completed · Overdue"
          />
          <Term
            term="QBR: On Track"
            def="Score ≥ 2.5 / 10. Progressing at or above expected rate."
          />
          <Term
            term="QBR: Behind"
            def="Score ≥ 1.875 / 10. Slightly below expected pace."
          />
          <Term
            term="QBR: Far Behind"
            def="Score ≥ 1.25 / 10. Significant underperformance."
          />
          <Term
            term="QBR: Red Flag"
            def="Score < 1.25 / 10. Critically off-track."
          />
          <Term
            term="Block states"
            def="Empty · In Progress · Done · Incomplete"
          />
          <Term
            term="Roadmap build phases"
            def="Data Gathering → Synthesis → Peak Ambition → Objectives → Gap Filling → Draft Ready"
          />
        </Section>

        <Section title="Notification Types (Internal)">
          <Term term="share" def="Something was shared with the user." />
          <Term term="comment" def="A comment was left on a document." />
          <Term term="ai_complete" def="An AI task has finished." />
          <Term
            term="spending_limit"
            def="Platform spending limit alert."
          />
          <Term
            term="broadcast"
            def="Platform-wide announcement from admin."
          />
          <Term
            term="workflow_assignment"
            def="User was assigned a workflow."
          />
          <Term
            term="workflow_artifact_ready"
            def="Workflow output is ready to view."
          />
          <Term
            term="ref_type"
            def="Internal field indicating the entity type a notification references."
          />
        </Section>

        <Section title="Roles & Access">
          <Term
            term="Super Admin"
            def="Highest platform-level access. Vojomo team only."
          />
          <Term
            term="Admin / Company Admin"
            def="Manages a company's account and members."
          />
          <Term term="Member" def="Standard platform user within a company." />
          <Term
            term="Owner / Creator"
            def="The user who created a roadmap or document. Has full control."
          />
          <Term
            term="Advisor"
            def="User assigned to advise on a session. Always sees all contributor data regardless of visibility settings. Managed via Session Settings drawer. Surfaces 'Sessions I'm Advising' on home."
          />
          <Term
            term="Team Member"
            def="Collaboration role (internal: team_member). Full edit + read rights. Can edit blocks, view collaborator list, view alignment reports, see pending contributions. One of the two invite-dropdown roles (the default). Shown in 'Team Roadmaps' on My Roadmaps."
          />
          <Term
            term="Owner (co-owner)"
            def="Full owner rights (internal: owner) — edit everything, invite others, run synthesis, lock periods, publish. Reached by creating the roadmap, being invited as Owner (one of the two invite-dropdown roles), or being promoted from Team Member."
          />
          <Term
            term="Contributor"
            def="Legacy collaboration role (internal: solo_contributor). Submits own version of building block content for synthesis. No longer offered at invite time (the dropdown is now Owner + Team Member); retained for existing rows. Shown as 'Contributor' in Shared With Me."
          />
          <Term
            term="Reviewer"
            def="Legacy invite grouping for read-focused access — mapped to comment ('Can view & comment') or view ('View only'). No longer offered at invite time; retained for existing rows."
          />
          <Term
            term="Collaborator"
            def="Any user with shared access to a roadmap or document."
          />
          <Term
            term="view"
            def="Read-only access level (internal string). No longer offered at invite time; retained for existing collaborator rows."
          />
          <Term
            term="comment"
            def="Read + comment access level (internal string). No longer offered at invite time; retained for existing collaborator rows."
          />
          <Term
            term="Write"
            def="Full edit access level."
          />
        </Section>

        <Section title="Operating Tools & QBR">
          <Term
            term="Operating Tools"
            def="Platform section containing QBR Scoring and Weekly Progress."
          />
          <Term
            term="QBR / Quarterly Business Review"
            def="Structured review of progress against annual objectives. Score scale: 0–10 (0%–100%)."
          />
          <Term
            term="Annual Objective Scoring"
            def="The scoring mechanism used in QBR reviews."
          />
          <Term
            term="Solo mode"
            def="View mode in the QBR/Progress panel showing only the current user's own scores and notes. Toggled via PillToggle (Solo / Team)."
          />
          <Term
            term="Team mode"
            def="View mode in the QBR/Progress panel showing all team contributor data alongside the user's own."
          />
          <Term
            term="PillToggle"
            def="Internal UI component used to toggle between Solo and Team view modes in the progress panel."
          />
          <Term
            term="Period Navigator"
            def="Prev/next arrow control in the progress panel for navigating between weeks or quarters."
          />
          <Term
            term="TAR strip"
            def="Collapsible section header in the progress panel labeled 'Team Alignment Report.' Visible to advisors and owners only."
          />
          <Term
            term="Re-run"
            def="Button inside the TAR strip to regenerate the alignment report from the latest contributions."
          />
          <Term
            term="Personal QBR"
            def="Individual user's QBR view."
          />
          <Term
            term="Team QBR"
            def="Company-wide QBR view."
          />
          <Term
            term="Scoring Legend"
            def="Key explaining QBR threshold statuses and color codes."
          />
          <Term
            term="Weekly Progress"
            def="Tool for tracking short-term momentum against objectives week-by-week."
          />
        </Section>

        <Section title="Roadmap Builder Features">
          <Term
            term="Roadmap Import"
            def="Multi-format import on the New Roadmap page. Mode toggle: 'Build from scratch' / 'Import'. JSON parsed client-side; PDF/text → POST /api/rb/parse-import (PyPDF2 + Claude)."
          />
          <Term
            term="Research / Research History"
            def="Vo background research via Globe icon. Tracks runs with summaries. Refresh: POST /api/rb/sessions/:id/refresh-research."
          />
          <Term
            term="VoInput"
            def="Reusable voice-first input component: Talk with Vo button + textarea + inline send icon. Path: frontend/app/src/components/VoInput/VoInput.tsx."
          />
          <Term
            term="Invite role dropdown"
            def="Two options: Owner · Team Member (default). Internal: inviteRoleGroup ('owner' | 'team_member'). Selecting Owner sends accessLevel 'owner' and grants full co-owner rights on accept. Legacy Reviewer/Contributor options were removed."
          />
        </Section>

        <Section title="Product Names & Branding">
          <Term term="Vojomo" def="The platform." />
          <Term term="Vo" def="The embedded AI assistant." />
          <Term term="VoChat" def="AI chat interface (Beta)." />
          <Term
            term="Vojomo Book"
            def="A roadmap type representing a company or initiative."
          />
          <Term
            term="Roadmap Builder"
            def="The core strategic tool for building a 7-block roadmap."
          />
          <Term
            term="Studio"
            def="Navigation section (formerly 'Documents') where all content artifacts live."
          />
          <Term
            term="Document Library"
            def="Where documents and folders are organized (within Studio)."
          />
          <Term
            term="Coordinator"
            def="The workflow management section of the platform."
          />
          <Term
            term="Operating Platform"
            def="The full product suite (Roadmap + Documents + Vo + Operating Tools + Coordinator)."
          />
          <Term
            term="VoInput"
            def="Reusable voice-first input component (Talk with Vo + textarea + send)."
          />
          <Term
            term="VoTile"
            def="UI pattern for compact tiles in a 2-column grid (RoadmapViewer, StudioHome)."
          />
          <Term
            term="VoCard"
            def="UI pattern for rich management cards in sm:2/lg:3 grid (My Roadmaps)."
          />
        </Section>

        <Section title="Time & Date References">
          <Term
            term="Quarter / Quarterly"
            def="Q1, Q2, Q3, Q4. Used in QBR Scoring and Quarterly Objectives."
          />
          <Term
            term="Year definition"
            def="A user/company setting that defines the start month of their fiscal year."
          />
          <Term
            term="Target peak date"
            def="The future date associated with a company's Peak Ambition (e.g. Dec 2030)."
          />
          <Term
            term="Deadline"
            def="Workflow assignment due date. Participants past this without submission are Overdue."
          />
          <Term
            term="{Days} left"
            def="Countdown shown on deleted items before permanent removal (30-day window)."
          />
        </Section>
      </div>
    </BaseLayout>
  );
}
